import type { Fragment, BuildContext } from "../types.js";
import { colors } from "../../brand/theme.js";
import { tl } from "../timeline.js";

type Variant = "primary" | "dark" | "light" | "accent";

const PALETTES: Record<Variant, readonly [string, string, string]> = {
  primary: [colors.primary, colors.secondary, colors.bg],
  dark: [colors.bg, "#111122", colors.primary + "55"],
  light: ["#ffffff", "#f4f4f8", colors.primary + "22"],
  accent: [colors.accent, colors.primary, colors.bg],
};

/**
 * Background gradient + soft radial highlights. Animated angle drift over the
 * full composition duration (135° → 165°).
 */
export function backgroundGradient(
  ctx: BuildContext,
  id: string,
  opts: { variant?: Variant; animated?: boolean } = {},
): Fragment {
  const variant = opts.variant ?? "dark";
  const animated = opts.animated ?? true;
  const stops = PALETTES[variant];

  const html = `<div id="${id}" class="bg-grad">
  <div class="bg-grad-base"></div>
  <div class="bg-grad-overlay"></div>
</div>`;

  const css = `
#${id} { position: absolute; inset: 0; pointer-events: none; }
#${id} .bg-grad-base { position: absolute; inset: 0; background: linear-gradient(135deg, ${stops[0]} 0%, ${stops[1]} 50%, ${stops[2]} 100%); }
#${id} .bg-grad-overlay { position: absolute; inset: 0; background: radial-gradient(circle at 30% 20%, rgba(255,255,255,0.08), transparent 55%), radial-gradient(circle at 80% 90%, rgba(255,255,255,0.05), transparent 60%); }
`;

  const tweens = animated
    ? [
        tl.fromTo(
          `#${id} .bg-grad-base`,
          { backgroundImage: `linear-gradient(135deg, ${stops[0]} 0%, ${stops[1]} 50%, ${stops[2]} 100%)` },
          {
            backgroundImage: `linear-gradient(165deg, ${stops[0]} 0%, ${stops[1]} 50%, ${stops[2]} 100%)`,
            duration: ctx.durationSec,
            ease: "none",
          },
          0,
        ),
      ]
    : [];

  return { html, css, tweens };
}
